/**
 * Dual-handle price range slider for the price facet.
 *
 * Sits inside `price-facet-component` and mirrors its min/max text inputs:
 * dragging a handle writes plain numeric values into the inputs, and the
 * native `change` event bubbling from the range inputs triggers the facet
 * component's existing `on:change="/updatePriceFilterAndResults"` handler.
 */
class CCPriceSlider extends HTMLElement {
  connectedCallback() {
    this.minRange = this.querySelector('[data-cc-range="min"]');
    this.maxRange = this.querySelector('[data-cc-range="max"]');
    this.facet = this.closest('price-facet-component');
    this.minInput = this.facet?.querySelector('[ref="minInput"]');
    this.maxInput = this.facet?.querySelector('[ref="maxInput"]');

    if (!this.minRange || !this.maxRange || !this.minInput || !this.maxInput) return;

    this.bound = this.#parse(this.dataset.rangeMax, 100);

    this.addEventListener('input', this.#onSliderInput);
    this.facet?.addEventListener('change', this.#onFacetChange);

    // Re-sync after the facets form is re-rendered in place (morph updates
    // the value attributes but never re-runs connectedCallback). Painting is
    // guarded to only write style when values change, so this can't loop.
    this.observer = new MutationObserver(() => this.#syncFromInputs());
    this.observer.observe(this, { attributes: true, subtree: true, attributeFilter: ['value', 'style'] });

    this.#syncFromInputs();
  }

  disconnectedCallback() {
    this.facet?.removeEventListener('change', this.#onFacetChange);
    this.observer?.disconnect();
  }

  /**
   * @param {unknown} value
   * @param {number} fallback
   * @returns {number}
   */
  #parse(value, fallback) {
    const parsed = Number.parseFloat(String(value ?? '').replace(/[^0-9.]/g, ''));
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  #onSliderInput = (event) => {
    const { minRange, maxRange, minInput, maxInput, bound } = this;
    let min = Number(minRange.value);
    let max = Number(maxRange.value);

    // Keep the handles from crossing
    if (min > max) {
      if (event.target === minRange) {
        min = max;
        minRange.value = String(min);
      } else {
        max = min;
        maxRange.value = String(max);
      }
    }

    // Empty input means "no filter" at that end of the range
    minInput.value = min <= 0 ? '' : String(min);
    maxInput.value = max >= bound ? '' : String(max);
    this.#paint(min, max);
  };

  #onFacetChange = (event) => {
    if (event.target === this.minRange || event.target === this.maxRange) return;
    this.#syncFromInputs();
  };

  #syncFromInputs() {
    const { minRange, maxRange, minInput, maxInput, bound } = this;
    const min = minInput.value.trim() === '' ? 0 : this.#parse(minInput.value, 0);
    const max = maxInput.value.trim() === '' ? bound : this.#parse(maxInput.value, bound);

    minRange.value = String(min);
    maxRange.value = String(max);
    this.#paint(min, max);
  }

  /**
   * @param {number} min
   * @param {number} max
   */
  #paint(min, max) {
    const { bound } = this;
    const start = bound > 0 ? (min / bound) * 100 : 0;
    const end = bound > 0 ? 100 - (max / bound) * 100 : 0;
    const startValue = `${Math.max(0, Math.min(100, start))}%`;
    const endValue = `${Math.max(0, Math.min(100, end))}%`;

    if (this.style.getPropertyValue('--cc-fill-start') !== startValue) {
      this.style.setProperty('--cc-fill-start', startValue);
    }
    if (this.style.getPropertyValue('--cc-fill-end') !== endValue) {
      this.style.setProperty('--cc-fill-end', endValue);
    }
  }
}

if (!customElements.get('cc-price-slider')) {
  customElements.define('cc-price-slider', CCPriceSlider);
}
